import {
  decodeBlobPayload,
  hydrateSnapshotHead,
  isRepositoryManifest,
  packSnapshotObjects,
  snapshotFromManifest,
  type RepositoryFile,
  type RepositorySnapshot,
} from '@trove/core';
import { decryptObject, encryptObject, type DeviceKeys } from '@trove/crypto';
import type { EncryptedObject } from '@trove/contracts';
import { postJson } from './api';

const decoder = new TextDecoder();

const BATCH = 100;

async function batchUpload(objects: EncryptedObject[]) {
  for (let index = 0; index < objects.length; index += BATCH) {
    await postJson('/v1/objects/batch', { objects: objects.slice(index, index + BATCH) });
  }
}

async function batchRead(ids: string[]): Promise<EncryptedObject[]> {
  const objects: EncryptedObject[] = [];
  for (let index = 0; index < ids.length; index += BATCH) {
    const result = await postJson<{ objects: EncryptedObject[] }>('/v1/objects/read', {
      ids: ids.slice(index, index + BATCH),
    });
    objects.push(...result.objects);
  }
  return objects;
}

function encryptFor(
  collectionKey: Uint8Array,
  collectionId: string,
  keys: Pick<DeviceKeys, 'signingPrivateKey' | 'signingPublicKey'>,
) {
  return (plaintext: Uint8Array, kind: EncryptedObject['kind']) =>
    encryptObject(
      plaintext,
      collectionKey,
      collectionId,
      kind,
      keys.signingPrivateKey,
      keys.signingPublicKey,
    );
}

export async function uploadRepositorySnapshot(
  snapshot: RepositorySnapshot,
  collectionKey: Uint8Array,
  collectionId: string,
  keys: Pick<DeviceKeys, 'signingPrivateKey' | 'signingPublicKey'>,
): Promise<{ headObjectId: string; snapshot: RepositorySnapshot }> {
  const packed = await packSnapshotObjects(snapshot, encryptFor(collectionKey, collectionId, keys));
  await batchUpload(packed.objects);
  const manifestObject = packed.objects.at(-1);
  if (!manifestObject || manifestObject.kind !== 'manifest') {
    throw new Error('Manifest object missing from packed upload');
  }
  return { headObjectId: manifestObject.objectId, snapshot: packed.snapshot };
}

async function readBlobMap(
  opaqueIds: string[],
  collectionKey: Uint8Array,
): Promise<Map<string, Uint8Array>> {
  const objects = await batchRead(opaqueIds);
  const map = new Map<string, Uint8Array>();
  for (const object of objects) {
    map.set(object.objectId, await decryptObject(object, collectionKey));
  }
  return map;
}

export async function loadSnapshotFromHeadObject(
  headObject: EncryptedObject,
  collectionKey: Uint8Array,
  options: { hydrateHead?: boolean } = { hydrateHead: true },
): Promise<RepositorySnapshot> {
  if (headObject.kind !== 'manifest') {
    throw new Error('Repository head must be a manifest object');
  }
  const parsed: unknown = JSON.parse(
    decoder.decode(await decryptObject(headObject, collectionKey)),
  );
  if (!isRepositoryManifest(parsed)) {
    throw new Error('Repository head is not a valid manifest');
  }
  let snapshot = snapshotFromManifest(parsed);
  if (options.hydrateHead !== false) {
    snapshot = await hydrateSnapshotHead(snapshot, (ids) => readBlobMap(ids, collectionKey));
  }
  return snapshot;
}

export async function ensureRepositoryFileContent(
  file: RepositoryFile,
  collectionKey: Uint8Array,
): Promise<RepositoryFile> {
  if (file.content !== undefined) return file;
  if (!file.blobOpaqueId) throw new Error(`No blob for ${file.path}`);
  const map = await readBlobMap([file.blobOpaqueId], collectionKey);
  const bytes = map.get(file.blobOpaqueId);
  if (!bytes) throw new Error(`Missing blob ${file.blobOpaqueId}`);
  const payload = decodeBlobPayload(bytes);
  return {
    ...file,
    mimeType: payload.mimeType,
    content: payload.content,
    size: new TextEncoder().encode(payload.content).length,
  };
}

export async function ensureSnapshotFilesContent(
  files: RepositoryFile[],
  collectionKey: Uint8Array,
): Promise<RepositoryFile[]> {
  const missing = files.filter((file) => file.content === undefined);
  if (!missing.length) return files;
  const ids = [...new Set(missing.map((file) => file.blobOpaqueId).filter(Boolean))] as string[];
  const map = await readBlobMap(ids, collectionKey);
  return files.map((file) => {
    if (file.content !== undefined) return file;
    if (!file.blobOpaqueId) throw new Error(`No blob for ${file.path}`);
    const bytes = map.get(file.blobOpaqueId);
    if (!bytes) throw new Error(`Missing blob ${file.blobOpaqueId}`);
    const payload = decodeBlobPayload(bytes);
    return {
      ...file,
      mimeType: payload.mimeType,
      content: payload.content,
      size: new TextEncoder().encode(payload.content).length,
    };
  });
}
